import type { ReactNode } from "react";

export interface TabItem {
  key: string;
  label: string;
  icon?: ReactNode;
  badge?: ReactNode;
  hint?: string;
}

interface BaseProps {
  tabs: TabItem[];
  active: string;
  onChange: (key: string) => void;
  className?: string;
}

/**
 * Horizontal segmented tabs with an underline indicator. Used inside content
 * panels (e.g. the Blog Detail content/image/social switcher).
 */
export function TabsBar({ tabs, active, onChange, className = "" }: BaseProps) {
  return (
    <div
      className={`border-b border-ink-100 flex gap-1 overflow-x-auto ${className}`}
      role="tablist"
    >
      {tabs.map((t) => {
        const selected = t.key === active;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(t.key)}
            className={[
              "relative inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium",
              "transition-colors whitespace-nowrap -mb-px border-b-2",
              selected
                ? "text-brand-700 border-brand-500"
                : "text-ink-500 border-transparent hover:text-ink-700",
            ].join(" ")}
          >
            {t.icon && <span className="shrink-0">{t.icon}</span>}
            {t.label}
            {t.badge}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Vertical sidebar tabs. Used for the Settings page — rail of section links on
 * the left, panel content on the right. Falls back to a horizontal bar on
 * narrow viewports.
 */
export function TabsRail({ tabs, active, onChange, className = "" }: BaseProps) {
  return (
    <>
      {/* Desktop: sidebar rail */}
      <nav
        className={`hidden md:block w-60 shrink-0 ${className}`}
        role="tablist"
        aria-orientation="vertical"
      >
        <ul className="space-y-0.5 sticky top-20">
          {tabs.map((t) => {
            const selected = t.key === active;
            return (
              <li key={t.key}>
                <button
                  role="tab"
                  aria-selected={selected}
                  onClick={() => onChange(t.key)}
                  className={[
                    "w-full text-left text-sm font-medium rounded-md",
                    "flex items-center gap-2.5 pl-3 pr-3 py-2 transition-colors",
                    "border-l-2",
                    selected
                      ? "bg-brand-50 text-brand-800 border-brand-500"
                      : "text-ink-600 hover:text-ink-900 hover:bg-ink-50 border-transparent",
                  ].join(" ")}
                >
                  {t.icon && (
                    <span
                      className={`shrink-0 ${
                        selected ? "text-brand-600" : "text-ink-400"
                      }`}
                    >
                      {t.icon}
                    </span>
                  )}
                  <span className="flex-1">{t.label}</span>
                  {t.badge}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Mobile: horizontal scroll bar */}
      <nav
        className="md:hidden mb-4 -mx-6 px-6 overflow-x-auto"
        role="tablist"
      >
        <div className="inline-flex gap-1 min-w-full border-b border-ink-100">
          {tabs.map((t) => {
            const selected = t.key === active;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={selected}
                onClick={() => onChange(t.key)}
                className={[
                  "inline-flex items-center gap-2 px-3 py-2 text-sm font-medium",
                  "whitespace-nowrap -mb-px border-b-2 transition-colors",
                  selected
                    ? "text-brand-700 border-brand-500"
                    : "text-ink-500 border-transparent hover:text-ink-700",
                ].join(" ")}
              >
                {t.icon && <span className="shrink-0">{t.icon}</span>}
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
